import { Pipe, PipeTransform } from '@angular/core';
import { DateTime as LuxonDateTime } from 'luxon';

@Pipe({
    name: 'parseDateTime',
    standalone: false
})

export class ParseDateTimePipe implements PipeTransform {

  //This pipe used to use moment.js. Instead of updating all formatting for luxon we do some mapping with this object:
  private momentToLuxonFormatMap = {
    'dddd': 'EEEE', // Full day name (Monday, Tuesday, etc.)
    'ddd': 'ccc',    // Short day name (Mon, Tue, etc.)
    'MMMM': 'LLLL',  // Full month name (January, February, etc.)
    'MMM': 'LLL',    // Short month name (Jan, Feb, etc.)
  };

  transform(value: string, format: string, timezone = undefined): string {
    try {
      if (!value) {
        return '-';
      }
      format = this.convertMomentFormatsToLuxon(format);
      let dateTime = LuxonDateTime.fromISO(value, { setZone: true });
      // If ISO parsing fails, try parsing as SQL date format (YYYY-MM-DD)
      if (!dateTime.isValid) {
        dateTime = LuxonDateTime.fromSQL(value);
      }
      if (!dateTime.isValid) {
        return '-';
      }
      if (timezone) {
        dateTime = dateTime.setZone(timezone);
      }
      const formattedDate = dateTime.toFormat(format);
      let translatedDate = this.translateDaysAndMonths(formattedDate);
      translatedDate = this.addYearIfLastYear(format, translatedDate, dateTime);
      return translatedDate;
    } catch (_error) {
      return '-';
    }
  }

  convertMomentFormatsToLuxon(format: string) {
    Object.keys(this.momentToLuxonFormatMap).forEach(momentToken => {
      if (format.includes(momentToken)) {
        format = format.replace(momentToken, this.momentToLuxonFormatMap[momentToken]);
      }
    });
    return format;
  }

  translateDaysAndMonths(formattedDate: string) {
    const language = localStorage.getItem('language') || 'en';
    Object.keys(translations).forEach(key => {
      if (translations[key][language] && formattedDate.includes(key)) {
        formattedDate = formattedDate.replace(key, translations[key][language]);
      }
    });
    return formattedDate;
  }

  addYearIfLastYear(format, formattedDate: string, dateTime: LuxonDateTime) {
    const isDateLastYear = dateTime.year < LuxonDateTime.now().year;
    if ((format.includes('LLL') || format.includes('MMM')) && isDateLastYear && !format.includes('yyyy') && !format.includes('yy')) {
      return `${formattedDate  }, ${  dateTime.toFormat('yyyy')}`;
    }
    return formattedDate;
  }
}

const translations = {
  'January': {
    en: 'January',
    es: 'Enero',
    ar: 'يناير',
    fr: 'Janvier',
    pt: 'Janeiro',
    de: 'Januar',
    ja: '1月',
    it: 'Gennaio'
  },
  'Jan': {
    en: 'Jan',
    es: 'Ene',
    ar: 'ينا',
    fr: 'Jan',
    pt: 'Jan',
    de: 'Jan',
    ja: '1月',
    it: 'Gen'
  },
  'February': {
    en: 'February',
    es: 'Febrero',
    ar: 'فبراير',
    fr: 'Février',
    pt: 'Fevereiro',
    de: 'Februar',
    ja: '2月',
    it: 'Febbraio'
  },
  'Feb': {
    en: 'Feb',
    es: 'Feb',
    ar: 'فبر',
    fr: 'Fév',
    pt: 'Fev',
    de: 'Feb',
    ja: '2月',
    it: 'Feb'
  },
  'March': {
    en: 'March',
    es: 'Marzo',
    ar: 'مارس',
    fr: 'Mars',
    pt: 'Março',
    de: 'März',
    ja: '3月',
    it: 'Marzo'
  },
  'Mar': {
    en: 'Mar',
    es: 'Mar',
    ar: 'مار',
    fr: 'Mar',
    pt: 'Mar',
    de: 'Mär',
    ja: '3月',
    it: 'Mar'
  },
  'April': {
    en: 'April',
    es: 'Abril',
    ar: 'أبريل',
    fr: 'Avril',
    pt: 'Abril',
    de: 'April',
    ja: '4月',
    it: 'Aprile'
  },
  'Apr': {
    en: 'Apr',
    es: 'Abr',
    ar: 'أبر',
    fr: 'Avr',
    pt: 'Abr',
    de: 'Apr',
    ja: '4月',
    it: 'Apr'
  },
  'May': {
    en: 'May',
    es: 'Mayo',
    ar: 'مايو',
    fr: 'Mai',
    pt: 'Maio',
    de: 'Mai',
    ja: '5月',
    it: 'Maggio'
  },
  'June': {
    en: 'June',
    es: 'Junio',
    ar: 'يونيو',
    fr: 'Juin',
    pt: 'Junho',
    de: 'Juni',
    ja: '6月',
    it: 'Giugno'
  },
  'Jun': {
    en: 'Jun',
    es: 'Jun',
    ar: 'يون',
    fr: 'Juin',
    pt: 'Jun',
    de: 'Jun',
    ja: '6月',
    it: 'Giu'
  },
  'July': {
    en: 'July',
    es: 'Julio',
    ar: 'يوليو',
    fr: 'Juillet',
    pt: 'Julho',
    de: 'Juli',
    ja: '7月',
    it: 'Luglio'
  },
  'Jul': {
    en: 'Jul',
    es: 'Jul',
    ar: 'يول',
    fr: 'Juil',
    pt: 'Jul',
    de: 'Jul',
    ja: '7月',
    it: 'Lug'
  },
  'August': {
    en: 'August',
    es: 'Agosto',
    ar: 'أغسطس',
    fr: 'Août',
    pt: 'Agosto',
    de: 'August',
    ja: '8月',
    it: 'Agosto'
  },
  'Aug': {
    en: 'Aug',
    es: 'Ago',
    ar: 'أغس',
    fr: 'Août',
    pt: 'Ago',
    de: 'Aug',
    ja: '8月',
    it: 'Ago'
  },
  'September': {
    en: 'September',
    es: 'Septiembre',
    ar: 'سبتمبر',
    fr: 'Septembre',
    pt: 'Setembro',
    de: 'September',
    ja: '9月',
    it: 'Settembre'
  },
  'Sep': {
    en: 'Sep',
    es: 'Sep',
    ar: 'سبت',
    fr: 'Sep',
    pt: 'Set',
    de: 'Sep',
    ja: '9月',
    it: 'Set'
  },
  'October': {
    en: 'October',
    es: 'Octubre',
    ar: 'أكتوبر',
    fr: 'Octobre',
    pt: 'Outubro',
    de: 'Oktober',
    ja: '10月',
    it: 'Ottobre'
  },
  'Oct': {
    en: 'Oct',
    es: 'Oct',
    ar: 'أكت',
    fr: 'Oct',
    pt: 'Out',
    de: 'Okt',
    ja: '10月',
    it: 'Ott'
  },
  'November': {
    en: 'November',
    es: 'Noviembre',
    ar: 'نوفمبر',
    fr: 'Novembre',
    pt: 'Novembro',
    de: 'November',
    ja: '11月',
    it: 'Novembre'
  },
  'Nov': {
    en: 'Nov',
    es: 'Nov',
    ar: 'نوف',
    fr: 'Nov',
    pt: 'Nov',
    de: 'Nov',
    ja: '11月',
    it: 'Nov'
  },
  'December': {
    en: 'December',
    es: 'Diciembre',
    ar: 'ديسمبر',
    fr: 'Décembre',
    pt: 'Dezembro',
    de: 'Dezember',
    ja: '12月',
    it: 'Dicembre'
  },
  'Dec': {
    en: 'Dec',
    es: 'Dic',
    ar: 'ديس',
    fr: 'Déc',
    pt: 'Dez',
    de: 'Dez',
    ja: '12月',
    it: 'Dic'
  },
  'Monday': {
    en: 'Monday',
    es: 'Lunes',
    ar: 'الاثنين',
    fr: 'Lundi',
    pt: 'Segunda-feira',
    de: 'Montag',
    ja: '月曜日',
    it: 'Lunedì'
  },
  'Mon': {
    en: 'Mon',
    es: 'Lun',
    ar: 'الا',
    fr: 'Lun',
    pt: 'Seg',
    de: 'Mon',
    ja: '月',
    it: 'Lun'
  },
  'Tuesday': {
    en: 'Tuesday',
    es: 'Martes',
    ar: 'الثلاثاء',
    fr: 'Mardi',
    pt: 'Terça-feira',
    de: 'Dienstag',
    ja: '火曜日',
    it: 'Martedì'
  },
  'Tue': {
    en: 'Tue',
    es: 'Mar',
    ar: 'الث',
    fr: 'Mar',
    pt: 'Ter',
    de: 'Die',
    ja: '火',
    it: 'Mar'
  },
  'Wednesday': {
    en: 'Wednesday',
    es: 'Miércoles',
    ar: 'الأربعاء',
    fr: 'Mercredi',
    pt: 'Quarta-feira',
    de: 'Mittwoch',
    ja: '水曜日',
    it: 'Mercoledì'
  },
  'Wed': {
    en: 'Wed',
    es: 'Mié',
    ar: 'الأ',
    fr: 'Mer',
    pt: 'Qua',
    de: 'Mit',
    ja: '水',
    it: 'Mer'
  },
  'Thursday': {
    en: 'Thursday',
    es: 'Jueves',
    ar: 'الخميس',
    fr: 'Jeudi',
    pt: 'Quinta-feira',
    de: 'Donnerstag',
    ja: '木曜日',
    it: 'Giovedì'
  },
  'Thu': {
    en: 'Thu',
    es: 'Jue',
    ar: 'الخ',
    fr: 'Jeu',
    pt: 'Qui',
    de: 'Don',
    ja: '木',
    it: 'Gio'
  },
  'Friday': {
    en: 'Friday',
    es: 'Viernes',
    ar: 'الجمعة',
    fr: 'Vendredi',
    pt: 'Sexta-feira',
    de: 'Freitag',
    ja: '金曜日',
    it: 'Venerdì'
  },
  'Fri': {
    en: 'Fri',
    es: 'Vie',
    ar: 'الج',
    fr: 'Ven',
    pt: 'Sex',
    de: 'Fre',
    ja: '金',
    it: 'Ven'
  },
  'Saturday': {
    en: 'Saturday',
    es: 'Sábado',
    ar: 'السبت',
    fr: 'Samedi',
    pt: 'Sábado',
    de: 'Samstag',
    ja: '土曜日',
    it: 'Sabato'
  },
  'Sat': {
    en: 'Sat',
    es: 'Sáb',
    ar: 'الس',
    fr: 'Sam',
    pt: 'Sáb',
    de: 'Sam',
    ja: '土',
    it: 'Sab'
  },
  'Sunday': {
    en: 'Sunday',
    es: 'Domingo',
    ar: 'الأحد',
    fr: 'Dimanche',
    pt: 'Domingo',
    de: 'Sonntag',
    ja: '日曜日',
    it: 'Domenica'
  },
  'Sun': {
    en: 'Sun',
    es: 'Dom',
    ar: 'الأ',
    fr: 'Dim',
    pt: 'Dom',
    de: 'Son',
    ja: '日',
    it: 'Dom'
  },
  'AM': {
    en: 'am',
    es: 'am',
    ar: 'ص',
    fr: 'am',
    pt: 'am',
    de: 'am',
    ja: 'am',
    it: 'am'
  },
  'PM': {
    en: 'pm',
    es: 'pm',
    ar: 'م',
    fr: 'pm',
    pt: 'pm',
    de: 'pm',
    ja: 'pm',
    it: 'pm'
  }

};
